import { z } from 'zod';

const DOMAIN_RE = /^(?=.{1,253}$)(?!-)([a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/;

/** Lower-cases, trims and strips a trailing dot. Returns null if invalid. */
export function normalizeDomain(input: string): string | null {
  const d = input.trim().toLowerCase().replace(/\.$/, '');
  return DOMAIN_RE.test(d) ? d : null;
}

export const domainSchema = z.string().transform((v, ctx) => {
  const d = normalizeDomain(v);
  if (!d) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid domain' });
    return z.NEVER;
  }
  return d;
});

/** The domain part of an email address, normalized, or null. */
export function emailDomain(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  return normalizeDomain(email.slice(at + 1));
}

export interface VerifiedDomain<T = string> {
  domain: string;
  orgId: T;
}

/**
 * Picks the org that owns an email address. An email matches a verified
 * domain when its domain equals it or is a subdomain of it
 * (`students.iitx.ac.in` matches `iitx.ac.in`). When several match, the most
 * specific (longest) domain wins, so an affiliated college with its own
 * domain beats its parent university.
 */
export function resolveDomainOwner<T>(
  email: string,
  verified: ReadonlyArray<VerifiedDomain<T>>,
): VerifiedDomain<T> | null {
  const d = emailDomain(email);
  if (!d) return null;
  let best: VerifiedDomain<T> | null = null;
  for (const v of verified) {
    if (d === v.domain || d.endsWith(`.${v.domain}`)) {
      if (!best || v.domain.length > best.domain.length) best = v;
    }
  }
  return best;
}

/** DNS TXT record name and value an org publishes to prove domain ownership. */
export function domainVerificationRecord(domain: string, token: string) {
  return {
    name: `_nirvik-ready.${domain}`,
    value: `nirvik-ready-verification=${token}`,
  };
}

/**
 * Public email providers can never be claimed by an org: anyone can get an
 * address there, so it proves nothing about affiliation.
 */
export const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'yahoo.com',
  'yahoo.co.in',
  'icloud.com',
  'me.com',
  'proton.me',
  'protonmail.com',
  'rediffmail.com',
  'aol.com',
  'zoho.com',
]);

export function isPublicEmailDomain(domain: string): boolean {
  return PUBLIC_EMAIL_DOMAINS.has(domain);
}
